<template>
  <main class="homepage-content" :class="{ desktop: $isDesktop }">
    <section
      class="section destinations"
      :class="[$i18n.locale === 'en-gb' ? 'en-gb' : '']"
    >
      <section class="tabs">
        <translate
          tag="h2"
          class="heading"
          :class="[isActive('popular') ? 'active' : 'inactive']"
          @click="setActive('popular')"
        >
          Popular searches
        </translate>
        <translate
          class="heading"
          :class="[isActive('destinations') ? 'active' : 'inactive']"
          @click="setActive('destinations')"
        >
          Top destinations
        </translate>
      </section>
      <popular-searches v-show="activeItem === 'popular'" class="tab-content" />
      <top-destinations
        v-show="activeItem === 'destinations'"
        class="tab-content"
      />
    </section>

    <section class="section categories">
      <translate tag="h3" class="heading"> Accommodation types </translate>
      <accommodation-types :categories-data="accomodationCategoriesData" />
    </section>

    <!-- below the fold -->

    <section class="section features">
      <translate tag="h3" class="heading"> Popular features </translate>
      <popular-features />
    </section>

    <section
      v-if="['en-gb', 'fr', 'de', 'nl', 'it', 'es'].includes($i18n?.locale)"
      class="section"
    >
      <translate tag="h3" class="heading"> National parks </translate>
      <national-parks />
    </section>

    <section class="near-me-section">
      <pu-search-link
        hierarchy-path="near-me/"
        category="glamping"
        exclude-type="nearMe"
        class="banner glamping"
        :modify-filters="{ facets: undefined }"
      >
        <img
          loading="lazy"
          src="@/assets/near-me/glamping-near-me.jpg"
          :alt="$t('Glamping near me')"
          width="360"
          height="242"
          class="backgnd"
        />
        <div>
          <img
            src="@/assets/images/map-pin.svg"
            width="15"
            height="18"
            alt=""
          />
          <translate>Glamping near me</translate>
        </div>
      </pu-search-link>

      <pu-search-link
        hierarchy-path="near-me/"
        category="campsites"
        exclude-type="nearMe"
        class="banner camping"
        :modify-filters="{ facets: undefined }"
      >
        <img
          loading="lazy"
          src="@/assets/homepage/camping-near-me.jpg"
          :alt="$t('Camping near me')"
          width="360"
          height="242"
          class="backgnd"
        />
        <div>
          <img
            src="@/assets/images/map-pin.svg"
            width="15"
            height="18"
            alt=""
          />
          <translate>Camping near me</translate>
        </div>
      </pu-search-link>
    </section>
    <div class="near-me-additional-links">
      <pu-search-link
        hierarchy-path="near-me/"
        category="caravans"
        :modify-filters="{ facets: undefined, categoryIds: undefined }"
        exclude-type="newLocation"
      >
        <img src="@/assets/images/map-pin.svg" width="15" height="18" alt="" />
        <translate>Caravan parks near me</translate>
      </pu-search-link>

      <pu-search-link
        hierarchy-path="near-me/"
        category="tourers"
        :modify-filters="{ facets: undefined, categoryIds: undefined }"
        exclude-type="newLocation"
      >
        <img src="@/assets/images/map-pin.svg" width="15" height="18" alt="" />
        <translate>Touring caravan sites near me</translate>
      </pu-search-link>

      <pu-search-link
        hierarchy-path="near-me/"
        category="lodges"
        :modify-filters="{ facets: undefined, categoryIds: undefined }"
        exclude-type="newLocation"
      >
        <img src="@/assets/images/map-pin.svg" width="15" height="18" alt="" />
        <translate>Lodges near me</translate>
      </pu-search-link>
    </div>

    <span class="section about">
      <about-pitchup v-if="$isDesktop" />
      <campsite-owner />
    </span>
    <template v-if="$isDesktop">
      <social-proof v-if="$i18n.locale === 'en-gb'" />
      <lazy-social-proof-fr v-else-if="$i18n.locale === 'fr'" />
      <lazy-social-proof-it v-else-if="$i18n.locale === 'it'" />
      <lazy-social-proof-es v-else-if="$i18n.locale === 'es'" />
      <lazy-social-proof-usa v-else-if="$i18n.locale === 'en-us'" />
      <lazy-social-proof-others v-else />
    </template>

    <section v-if="shouldShowArticles" class="section articles">
      <div class="articles-header">
        <translate tag="h3" class="heading"> How to's & guides </translate>
      </div>

      <article-list />
    </section>

    <template v-if="!$isDesktop">
      <span class="section about">
        <about-pitchup />
      </span>
      <social-proof v-if="$i18n.locale === 'en-gb'" />
      <lazy-social-proof-fr v-else-if="$i18n.locale === 'fr'" />
      <lazy-social-proof-it v-else-if="$i18n.locale === 'it'" />
      <lazy-social-proof-es v-else-if="$i18n.locale === 'es'" />
      <lazy-social-proof-usa v-else-if="$i18n.locale === 'en-us'" />
      <lazy-social-proof-others v-else />
    </template>

    <footer-seo-links />
  </main>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
/* TODO: Add proper typing to resolve type safety warnings */
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'
import { CampsiteCategoriesPresenter } from '~/apps/categories/CampsiteCategoriesPresenter'

interface HorizontalCard {
  name: string
  to: { facet?: string; category: string }
  imgUrl?: string
  iconUrl?: string
}

@Component({
  setup: () => {
    useHead({
      bodyAttrs: {
        class: 'homepage',
      },
    })
  },
})
class HomepageMain extends Vue {
  get shouldShowArticles() {
    return this.$i18n?.locale.startsWith('en-')
  }

  get accomodationCategoriesData(): HorizontalCard[] {
    const categoriesPresenter = getCampsiteCategoriesPresenter(
      this.$route.params.lang || 'en-gb',
    )
    const categories = categoriesPresenter.bySlugMap
    const sliderData: HorizontalCard[] = []
    for (const [index, [_, category]] of Object.entries(categories).entries()) {
      // glamping is still not a proper category so need to push it based on the required index
      if (index === 4) {
        sliderData.push(this.getGlampingCategory(categoriesPresenter))
      }
      if (category.id.toString() !== '13') {
        sliderData.push(this.getCategorySliderData(category))
      }
    }
    return sliderData
  }

  private getCategorySliderData(
    category: typeof this.categoriesPresenter.bySlugMap.type,
  ) {
    return {
      name: category.pretty_name,
      to: { category: category.rootSlug },
      iconUrl: category.getImageDataUrl(),
      // TODO - has this change broken cachable assets? Change made by Pete
      // iconUrl: require(`@/assets/images/category-icons/${category.slug}.svg`),
    }
  }

  private getGlampingCategory(
    categoriesPresenter: CampsiteCategoriesPresenter,
  ): HorizontalCard {
    return {
      name: this.$t('Glamping'),
      to: { category: 'glamping' },
      iconUrl: categoriesPresenter.categoryImagesDataUrls.glamping,
    }
  }

  activeItem = 'popular'

  isActive(menuItem: string) {
    return this.activeItem === menuItem
  }

  setActive(menuItem: string) {
    this.activeItem = menuItem
  }
}

export default toNative(HomepageMain)
</script>

<style>
.homepage .app-footer {
  padding-top: 2em;
  min-height: 10em;
}

.homepage footer .feefo-badge,
.homepage .menu-btn img {
  display: none;
}

.homepage.active-tab-search .menu-btn img,
.homepage.homepage-search-open .menu-btn img {
  display: block;
}

/* desktop & tablet global */
.homepage.desktop {
  max-width: var(--page-width-max);
  margin: 0 auto;
}

.homepage.desktop #page-wrapper {
  width: 100% !important;
  z-index: unset;
}

.homepage.desktop .app-header nav > ul {
  min-height: 9em;
}

@media (min-width: 768px) {
  .homepage.desktop .app-header .logo {
    width: 126px;
    height: 110px;
  }

  .homepage.desktop .app-header nav > ul > li:first-child {
    padding-left: 2em;
  }
}

@media (min-width: 1700px) {
  .homepage.desktop .app-header nav > ul {
    padding: 0;
  }

  .homepage.desktop .app-header nav > ul > li:first-child {
    padding-left: 0;
  }
}

@media (min-width: 1606px) {
  .homepage.desktop footer:not(.footer-seo-links) {
    border-radius: 1em;
    margin-bottom: 1em;
    overflow: hidden;
  }
}
</style>

<style scoped>
.homepage-content {
  position: relative;
  width: 100vw;
}

.active-tab-search .homepage-content {
  display: none;
}

.homepage-content :deep(.heading) {
  font-size: 1.2em;
  font-weight: normal;
  color: var(--colour-wcag-blue);
  margin: 0 0 0.75em 1em;
}

.strapline.heading {
  color: white;
  font-size: 1.4em;
  margin: 0 0 0.5em 0;
  display: block;
  line-height: 1;
}

.tabs {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 1.4em;
  position: relative;
  user-select: none;
}

.tabs::after {
  content: '';
  position: absolute;
  background-color: var(--colour-page-background);
  bottom: -8px;
  left: 1.4em;
  right: 1.4em;
  width: calc(100% - 2.8em);
  height: 3px;
  border-radius: 3px;
  z-index: 1;
}

.homepage-content .tabs .heading {
  margin: 0;
  line-height: 1;
  position: relative;
}

.tabs .heading:last-child {
  text-align: right;
}

.tabs .inactive {
  color: #888;
}

.tabs .heading::after {
  content: '';
  position: absolute;
  background-color: #185ba1;
  bottom: -8px;
  left: 0;
  width: 100%;
  height: 3px;
  border-radius: 3px;
  z-index: 2;
}

.tabs .inactive.heading::after {
  content: none;
}

.tab-content {
  margin: 0.7em 0 0 0;
}

.destinations,
.features {
  background: #f4f6f8;
}

.section {
  padding: 2em 0;
}

.section.destinations {
  padding-top: 3.5em;
  margin-top: -3.2em;
}

.section.join-us {
  padding: 1em 0 0;
}

.section .heading {
  margin: 0 0 0.15em 1.1em;
}

.near-me-section {
  margin: 0 auto;
}

.near-me-additional-links {
  padding: 1.4em;
  column-gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(10em, 1fr));
  display: grid;
  row-gap: 1em;
}

.near-me-additional-links a {
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 5em;
  color: var(--colour-wcag-blue);
  display: inline-flex;
  font-size: var(--body);
  font-weight: bold;
  margin: 0 auto;
  padding: 0.2em 0.75em 0.2em 0;
  position: relative;
  transition:
    box-shadow 0.15s ease-out,
    transform 0.2s ease-out;
  text-decoration: none;
  border: 4px solid var(--colour-page-background-light);
  line-height: 1.1;
}

.near-me-additional-links a:active {
  filter: brightness(0.5);
  transform: scale(1.1);
}

.near-me-additional-links img {
  vertical-align: middle;
  height: 2em;
  width: auto;
  margin: 0 0 0.1em 0;
}

.articles-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.articles-header a {
  font-size: var(--body);
  color: var(--colour-wcag-blue);
  padding-right: 1.4em;
}

.banner {
  align-items: flex-end;
  display: flex;
  justify-content: center;
  min-height: 15em;
  position: relative;
  text-decoration: none;
  overflow: hidden;
}

.banner:active {
  filter: brightness(0.5);
}

.banner img {
  position: absolute;
  width: 100%;
}

.banner img.backgnd {
  aspect-ratio: 3/2;
  object-fit: cover;
  height: 100%;
}

.banner div {
  align-items: center;
  background: white;
  border-radius: 5em;
  color: var(--colour-wcag-blue);
  display: inline-flex;
  font-size: 1.2em;
  font-weight: bold;
  justify-content: flex-start;
  margin: 1em auto;
  padding: 0.2em;
  position: relative;
  transition: all 0.2s;
  z-index: 2;
}

.banner div img {
  vertical-align: middle;
  height: 2.3em;
  width: auto;
  margin: 0 0 0.4em 0;
}

.banner div span {
  padding: 0.5em 1em 0.5em 2em;
}

.banner:active div {
  transform: scale(1.1);
}

.scrollable {
  overflow-x: auto;
  overflow-y: visible;
  padding: 1em 1.4em;
  scroll-padding: 0 1.4em;
  scroll-snap-type: x mandatory;
}

@supports (scrollbar-width: none) {
  .scrollable {
    scrollbar-width: none;
  }

  .desktop .scrollable {
    scrollbar-width: thin;
  }
}

@supports selector(::-webkit-scrollbar) {
  .scrollable:not(.desktop)::-webkit-scrollbar {
    display: none;
  }
}

.hero {
  display: none;
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .section.about {
    align-items: flex-start;
    column-gap: 2em;
    display: grid;
    justify-content: space-between;
    padding: 1.4em;
    isolation: isolate;
    grid-template-columns: repeat(auto-fill, minmax(18em, 1fr));
  }

  .about :deep(.about-us) {
    grid-column: 1/2;
    grid-row: 1/2;
    padding: 0;
  }

  .about :deep(.join-us) {
    border-radius: 1em;
    height: auto;
    overflow: hidden;
    margin: 0;
    padding: 0 0 1em 0;
  }

  .near-me-section {
    row-gap: 0;
    gap: 1.4em;
    display: grid;
    grid-auto-flow: dense;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    justify-content: space-evenly;
    margin: auto 1.4em;
  }

  .banner {
    border-radius: 0.7em;
  }
}

/* desktop & tablet */
.desktop.homepage-content {
  width: 100%;
}

.desktop.homepage-content :deep(.heading),
.desktop.homepage-content .tabs :deep(.heading) {
  font-size: 3em;
  font-weight: bold;
  margin: 0.75em auto;
  text-align: center;
}

.desktop .near-me-section {
  gap: 2em;
  padding: 5em 5em 0 5em;
  display: grid;
  grid-auto-flow: dense;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.desktop .banner {
  border-radius: 1em;
  min-height: 20em;
  transition:
    box-shadow 0.15s ease-out,
    transform 0.25s ease-out;
}

.desktop .banner:hover,
.desktop .banner:focus-visible {
  transform: scale(1.05);
  outline: none;
}

.desktop .banner div {
  font-size: 2em;
  z-index: unset;
}

.desktop .banner:hover div,
.desktop .banner:focus-visible div {
  background-color: #185ba1;
  color: white;
}

.desktop .near-me-additional-links {
  padding: 3em 5em 2em 5em;
  column-gap: 2em;
  grid-template-columns: repeat(auto-fit, minmax(14em, 1fr));
  row-gap: 1.5em;
}

.desktop .near-me-additional-links a {
  font-size: 1.4em;
}

.desktop .near-me-additional-links img {
  height: 2.3em;
  margin: 0 0 0.4em 0;
}

.desktop .near-me-additional-links a:hover,
.desktop .near-me-additional-links a:focus-visible {
  box-shadow: 0 0 0 4px var(--focus-ring-colour);
  outline: none;
  border-color: var(--focus-ring-colour);
  transform: scale(1.15);
}

.desktop .articles-header {
  justify-content: center;
}

.desktop.homepage-content :deep(.logo) {
  top: -130px;
  left: 100px;
  width: 110px;
}

.desktop .categories {
  padding: 2em 5em 5em;
}

.desktop .features {
  padding: 2em 0;
  position: relative;
}

.desktop .scrollable {
  padding: 2em 5em;
  scroll-padding: 0 5em;
}

.desktop .section.destinations {
  padding: 2em 0 0 0;
  position: relative;
  isolation: isolate;
  margin: 0;
}

.desktop .section.destinations > span {
  display: block !important;
}

.desktop .section.destinations:after,
.desktop .section.features:after {
  /* smooth gradient, remove banding */
  background-image: linear-gradient(
    to left,
    rgba(244, 246, 249, 1) 0%,
    rgba(244, 246, 249, 0.738) 19%,
    rgba(244, 246, 249, 0.541) 34%,
    rgba(244, 246, 249, 0.382) 47%,
    rgba(244, 246, 249, 0.278) 56.5%,
    rgba(244, 246, 249, 0.194) 65%,
    rgba(244, 246, 249, 0.126) 73%,
    rgba(244, 246, 249, 0.075) 80.2%,
    rgba(244, 246, 249, 0.042) 86.1%,
    rgba(244, 246, 249, 0.021) 91%,
    rgba(244, 246, 249, 0.008) 95.2%,
    rgba(244, 246, 249, 0.002) 98.2%,
    rgba(244, 246, 249, 0) 100%
  );
  content: '';
  height: 100%;
  right: 0;
  left: auto;
  top: 0;
  bottom: 0;
  position: absolute;
  width: 6em;
  z-index: 1;
  display: block;
  pointer-events: none;
}

.desktop.homepage-content :deep(.top-destinations.tab-content) {
  display: block !important;
  overflow: unset;
  margin: 0;
}

.desktop.homepage-content :deep(.destinations-list) {
  min-width: calc(100% - 10em);
  justify-content: center;
  padding: 4em 5em;
}

.desktop.homepage-content :deep(.destinations-list a) {
  font-size: 1.3em;
  min-width: 7em;
  max-width: 21em;
}

.desktop.homepage-content :deep(.destinations-list a) {
  transition: box-shadow 0.15s ease-out;
}

.desktop.homepage-content :deep(.destinations-list a span) {
  transition: transform 0.2s ease-out;
}

.desktop.homepage-content :deep(.destinations-list a:hover),
.desktop.homepage-content :deep(.destinations-list a:focus-visible) {
  box-shadow: 0 0 0 7px var(--focus-ring-colour);
  outline: none;
}

.desktop.homepage-content :deep(.destinations-list a:hover span),
.desktop.homepage-content :deep(.destinations-list a:focus-visible span) {
  transform: scale(1.15);
}

.desktop .tabs {
  justify-content: center;
}

.desktop .tabs::after,
.desktop .tabs .heading::after {
  content: none;
}

.desktop .tabs .heading:last-of-type {
  display: none;
}

.desktop .section.about {
  align-items: flex-start;
  gap: 2em;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  grid-auto-rows: auto;
  justify-content: space-between;
  padding: 4em 5em 5em 5em;
  isolation: isolate;
}

.desktop .about :deep(.about-us) {
  padding: 0;
}

.desktop .about :deep(.join-us) {
  border-radius: 1.5em;
  margin: 0;
}

.desktop .about :deep(.join-us h3) {
  font-size: 3em;
}

.desktop .about :deep(.join-us .more) {
  font-size: 1.3em;
}

.desktop .about :deep(.about-us .heading) {
  margin-top: 0;
}

.desktop .about :deep(.about-us p),
.desktop .about :deep(.about-us li) {
  font-size: inherit;
}

.desktop .about :deep(.about-us li) {
  padding-bottom: 1em;
}

.desktop .about :deep(.feefo-badge a) {
  justify-content: flex-start;
}

@media (min-width: 1606px) {
  .desktop .section.destinations {
    border-radius: 0 0 1em 1em;
    overflow: hidden;
  }

  .desktop .features,
  .desktop :deep(.social-proof) {
    border-radius: 1em;
    overflow: hidden;
  }
}
</style>
